import {Button, Card, DataStatus, openDialog, Separator} from 'platform/components';
import {Box, Heading, HStack, Show, VStack} from 'platform/foundation';

import {useGetOrderProfitabilityQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {usePermissions} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useWorkshopUrl} from '../../../../../hooks/useWorkshopUrl';
import {OrderTrackedTime} from './OrderTrackedTime';
import {ProfitabilityFinances} from './ProfitabilityFinances';
import {ProfitabilityWorkingTime} from './ProfitabilityWorkingTime';

// 400 px. When the tab is narrower than two cards and the gap, the cards wrap into one column.
const CARD_MIN_WIDTH = 100;

interface ProfitabilitySummaryProps extends TestIdProps {
  serviceCaseId: string;
  isReportButtonDisabled: boolean;
  canReadServiceOrderFinancesProfitability: boolean;
}

export function ProfitabilitySummary(props: ProfitabilitySummaryProps) {
  const {orderId, serviceCaseId} = useWorkshopUrl();

  const [canReadChronos] = usePermissions({
    permissionKeys: ['chronosUser'],
  });

  const {data, isLoading, isError} = useGetOrderProfitabilityQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: orderId,
  });

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={46}>
      <HStack spacing={4} wrap>
        <Show when={props.canReadServiceOrderFinancesProfitability}>
          <Box flexGrow={1} flexBasis={CARD_MIN_WIDTH} minWidth={0}>
            <Card variant="inlineGrey" isFullHeight>
              <VStack spacing={4}>
                <Heading size={4}>{i18n.t('entity.order.labels.finances')}</Heading>
                <Separator spacing={0} />
                <ProfitabilityFinances
                  profitabilityData={data}
                  data-testid={suffixTestId('finances', props)}
                />
              </VStack>
            </Card>
          </Box>
        </Show>
        <Box flexGrow={1} flexBasis={CARD_MIN_WIDTH} minWidth={0}>
          <Card variant="inlineGrey" isFullHeight>
            <VStack spacing={4}>
              <HStack justify="space-between" spacing={4}>
                <Heading size={4}>{i18n.t('entity.order.labels.workingTime')}</Heading>
                {/* Keeps the heading row as high as in the Finances card, so both separators line up. The 32 px link overflows it evenly. */}
                <HStack height={6} align="center">
                  <Button
                    title={i18n.t('entity.order.labels.report')}
                    variant="link"
                    isDisabled={props.isReportButtonDisabled || !canReadChronos}
                    leftIcon="devices/access_time"
                    data-testid={suffixTestId('report', props)}
                    onClick={() =>
                      openDialog(
                        <OrderTrackedTime serviceCaseId={serviceCaseId} serviceOrderId={orderId} />,
                        {title: i18n.t('entity.orderRequest.labels.workReport')}
                      )
                    }
                  />
                </HStack>
              </HStack>
              <Separator spacing={0} />
              <ProfitabilityWorkingTime
                canReadChronos={canReadChronos}
                profitabilityData={data}
                data-testid={suffixTestId('workingTime', props)}
              />
            </VStack>
          </Card>
        </Box>
      </HStack>
    </DataStatus>
  );
}
